import React, { useState, useEffect, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.css';
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Dropdown, Form, Modal, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import SectionLoader from '../../../components/SectionLoader';
import { Link } from 'react-router-dom';
import axios from 'axios';
import '../../../assets/scss/component.scss';
import { error, success } from "../../../notify";

const AuditLogs = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const ERR_NETWORK_MESSAGE = 'Server is not available.';
    const token = sessionStorage.getItem("revizeTrainingToken");

    // API endpoints - removed count API
    const auditLogsApi = `${process.env.REACT_APP_SERVER_URL}/api/website/audit_records`;
    const auditLogUsersApi = `${process.env.REACT_APP_SERVER_URL}/api/user/get_audit_log_users`;
    const setPageLimitURL = `${process.env.REACT_APP_SERVER_URL}/api/set-page-limit/`;

    // Predefined filter options
    const AUDIT_ENTITIES = ['Website', 'Training', 'Category', 'Category Document', 'User', 'External Link', 'Configuration'];
    const OPERATIONS = ['INSERT', 'UPDATE', 'DELETE', 'FST', 'RETRAIN', 'RETRAIN ALL'];
    const TIMESTAMP_OPTIONS = [
        { value: 'All', label: 'All' },
        { value: 'Today', label: 'Today' },
        { value: 'Yesterday', label: 'Yesterday' },
        { value: 'This Week', label: 'This Week' },
        { value: 'Previous Week', label: 'Previous Week' },
        { value: 'This Month', label: 'This Month' },
        { value: 'This Year', label: 'This Year' },
        { value: 'Past 5 Years', label: 'Past 5 Years' }
    ];

    // State management
    const [auditData, setAuditData] = useState([]);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentRangeStart, setCurrentRangeStart] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(() => {
        return sessionStorage.getItem("current_page_limit")
            ? parseInt(sessionStorage.getItem("current_page_limit"), 10)
            : 50;
    });

    // Add state to track which row is being hovered
    const [hoveredRowIndex, setHoveredRowIndex] = useState(null);

    // Multiselect filter states
    const [selectedAuditEntities, setSelectedAuditEntities] = useState([]);
    const [selectedOperations, setSelectedOperations] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [usersMap, setUsersMap] = useState({});

    // Single select timestamp state - changed from array to string
    const [selectedTimestamp, setSelectedTimestamp] = useState("");

    // Search states
    const [searchInputValue, setSearchInputValue] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [showClearButton, setShowClearButton] = useState(false);

    const loggedInUserId = sessionStorage.getItem("loggedInUserId");
    const initialRenderOfSetPageLimitURL = useRef(true);

    // Use ref to prevent multiple API calls
    const isSearchingRef = useRef(false);

    // Dropdown options
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(true);

    const pagesToShow = 10;
    const offset = useRef(0);

    // Fetch site details from local storage or navigation state 
    const [siteId, setSiteId] = useState('');
    const [siteName, setSiteName] = useState('');

    // Icons
    const SearchIcon = () => (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
        </svg>
    );

    const ClearIcon = () => (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    );

    const RefreshIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5a6acf" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 2v6h6"></path>
            <path d="M21 12a9 9 0 0 0-15-6.7L3 8"></path>
            <path d="M21 22v-6h-6"></path>
            <path d="M3 12a9 9 0 0 0 15 6.7L21 16"></path>
        </svg>
    );

    const BackIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 9 12 15 6"></polyline>
            <line x1="5" y1="6" x2="5" y2="18"></line>
        </svg>
    );

    // Function to get operation badge style
    const getOperationBadgeStyle = (operation) => {
        const op = operation.toUpperCase();
        
        // Blue for UPDATE, RETRAIN, RETRAIN ALL
        if (op === 'UPDATE' || op === 'RETRAIN' || op === 'RETRAIN ALL') {
            return {
                backgroundColor: '#e7f1ff',
                color: '#0d6efd',
                border: '1px solid #0d6efd'
            };
        }
        // Red for DELETE
        else if (op === 'DELETE') {
            return {
                backgroundColor: '#f8d7da',
                color: '#dc3545',
                border: '1px solid #dc3545'
            };
        }
        // Green for INSERT, FST
        else if (op === 'INSERT' || op === 'FST') {
            return {
                backgroundColor: '#d1e7dd',
                color: '#198754',
                border: '1px solid #198754'
            };
        }
        // Default color for any other operations
        else {
            return {
                backgroundColor: '#e2e3e5',
                color: '#6c757d',
                border: '1px solid #6c757d'
            };
        }
    };

    useEffect(() => {
        // Try to get site details from location state first
        if (location.state && location.state.siteId) {
            setSiteId(location.state.siteId);
            setSiteName(location.state.siteName);
        } else {
            // Fallback to local storage or navigate back if no details
            const storedData = localStorage.getItem("auditLogsParams");
            if (storedData) {
                const auditLogsParams = JSON.parse(storedData);
                setSiteId(auditLogsParams.siteId || '');
                setSiteName(auditLogsParams.siteName || '');
            } else {
                navigate('/site');
            }
        }
    }, [location.state, navigate]);

    useEffect(() => {
        const setPageLimit = async () => {
            try {
                await axios.post(
                    setPageLimitURL,
                    { page_limit: itemsPerPage },
                    {
                        headers: { "Authorization": `Bearer ${token}` },
                        params: { user_id: loggedInUserId }
                    }
                );
                sessionStorage.setItem('current_page_limit', itemsPerPage);
            } catch (err) {
                console.error('Error setting page limit:', err);
            }
        };

        if (initialRenderOfSetPageLimitURL.current) {
            initialRenderOfSetPageLimitURL.current = false;
        } else {
            setPageLimit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemsPerPage]);

    // Fetch users for the filter
    useEffect(() => {
        const fetchUsers = async () => {
            if (!siteId || !token) return;

            setUsersLoading(true);
            try {
                const response = await axios.get(
                    auditLogUsersApi,
                    {
                        headers: { "Authorization": `Bearer ${token}` },
                        params: { website_id: siteId }
                    }
                );

                // Process users data from response
                const usersData = response.data;

                // Store the complete mapping of username -> ID
                setUsersMap(usersData);

                // Convert the object keys to an array of usernames (as before)
                const usersList = Object.keys(usersData);
                setUsers(usersList);
                setUsersLoading(false);
            } catch (err) {
                console.error("Error fetching users:", err);
                setUsersLoading(false);
                if (err.code === 'ERR_NETWORK') {
                    error(ERR_NETWORK_MESSAGE, true);
                } else {
                    error(err.response?.data?.detail || err.message, true);
                }
            }
        };

        fetchUsers();
    }, [token, siteId, auditLogUsersApi]);

    // Toggle selection for filters
    const toggleSelection = (value, currentSelections, setSelections) => {
        setSelections(
            currentSelections.includes(value)
                ? currentSelections.filter(item => item !== value)
                : [...currentSelections, value]
        );
    };

    // Search functionality
    const handleSearch = async () => {
        if (isSearchingRef.current) return;
        
        isSearchingRef.current = true;
        
        const searchValue = searchInputValue.trim();
        if (searchValue !== "") {
            setShowClearButton(true);
        }
        
        // Update searchTerm state first
        setSearchTerm(searchValue);
        setCurrentPage(1);
        setCurrentRangeStart(1);
        
        const newOffset = 0;
        offset.current = newOffset;
        
        // Force a state update and then fetch data
        await fetchData(newOffset, searchValue);
        
        isSearchingRef.current = false;
    };

    const handleClearSearch = async () => {
        if (isSearchingRef.current) return;
        
        isSearchingRef.current = true;
        
        // Clear both input value and search term
        setSearchInputValue("");
        setSearchTerm("");
        setShowClearButton(false);
        setCurrentPage(1);
        setCurrentRangeStart(1);
        
        const newOffset = 0;
        offset.current = newOffset;
        
        // Fetch data with empty search
        await fetchData(newOffset, "");
        
        isSearchingRef.current = false;
    };

    const fetchData = async (customOffset = null, customSearchTerm = null) => {
        setIsLoading(true);
        const currentOffset = customOffset !== null ? customOffset : (currentPage - 1) * itemsPerPage;
        offset.current = currentOffset;

        // Use customSearchTerm if provided, otherwise use the current searchTerm state
        const currentSearchTerm = customSearchTerm !== null ? customSearchTerm : searchTerm;

        try {
            // Convert selected usernames to their corresponding IDs
            const selectedUserIds = selectedUsers.map(username => usersMap[username] || username);

            const params = {
                website_id: siteId,
                offset: currentOffset,
                limit: itemsPerPage,
                audit_entity: selectedAuditEntities,
                audit_operation: selectedOperations,
                user_id: selectedUserIds,
                time_stamp: selectedTimestamp ? [selectedTimestamp] : [],
                search_text: currentSearchTerm ? currentSearchTerm.trim() : ""
            };

            console.log("Params before request:", params);

            const response = await axios.get(auditLogsApi, {
                headers: {
                    "Authorization": `Bearer ${token}`
                },
                params: params,
                paramsSerializer: {
                    indexes: null
                }
            });

            // Set both records and total count from the single response
            setAuditData(response.data.records || []);
            setTotalRecords(response.data.total_count || 0);
            setIsLoading(false);

        } catch (err) {
            console.error("Error fetching data:", err);
            setIsLoading(false);
            if (err.code === 'ERR_NETWORK') {
                error(ERR_NETWORK_MESSAGE);
            } else {
                error(err.response?.data?.detail || err.message, true);
            }
            navigate('/site');
        }
    };

    // Fetch data when pagination or filters change (excluding searchTerm)
    useEffect(() => {
        if (token && siteId && !isSearchingRef.current) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, siteId, currentPage, itemsPerPage,
        selectedAuditEntities, selectedOperations,
        selectedUsers, selectedTimestamp,
        navigate]);

    // Pagination handlers
    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        if (pageNumber < currentRangeStart || pageNumber >= currentRangeStart + pagesToShow) {
            setCurrentRangeStart(Math.floor((pageNumber - 1) / pagesToShow) * pagesToShow + 1);
        }
    };


    const handleItemsPerPageChange = (e) => {
        const newLimit = parseInt(e.target.value, 10);
        setItemsPerPage(newLimit);
        setCurrentPage(1);
        setCurrentRangeStart(1);
        sessionStorage.setItem('current_audit_logs_page_limit', newLimit);
    };

    // Format timestamp consistently
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return "N/A";
        const date = new Date(timestamp);

        const formattedDate = date.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "2-digit",
        });

        const formattedTime = date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });

        return `${formattedDate}, ${formattedTime}`;
    };

    // Open record details modal
    const openRecordModal = (record) => {
        setSelectedRecord(record);
        setShowModal(true);
    };

    const closeRecordModal = () => {
        setShowModal(false);
        setSelectedRecord(null);
    };

    const refreshData = () => {
        fetchData();
        success("Audit logs refreshed successfully.",true);
    }

    const renderTableContent = () => {
        if (isLoading) {
            return (
                <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '14px 10px' }}>
                        <SectionLoader label="Loading audit logs..." className="revize-section-loader--table" />
                    </td>
                </tr>
            );
        }

        if (auditData.length === 0) {
            return (
                <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '14px 10px' }}>
                        {searchTerm ? 'No audit logs found matching your search.' : 'No audit logs found'}
                    </td>
                </tr>
            );
        }

        return auditData.map((item, index) => {
            const operationStyle = getOperationBadgeStyle(item.operation);
            
            return (
                <tr
                    key={index}
                    onClick={() => openRecordModal(item)}
                    onMouseEnter={() => setHoveredRowIndex(index)}
                    onMouseLeave={() => setHoveredRowIndex(null)}
                    style={{ cursor: 'pointer' }}
                >
                    <td style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0' }}>{item.user_name}</td>
                    <td style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0' }}>{item.audit_entity}</td>
                    <td style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0' }}>
                        <span
                            style={{
                                display: 'inline-block',
                                padding: '4px 12px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '600',
                                ...operationStyle
                            }}
                        >
                            {item.operation}
                        </span>
                    </td>
                    <td style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0' }}>
                        {item.entity_name.length > 50 ? item.entity_name.substring(0, 50) + '...' : item.entity_name}
                    </td>
                    <td style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                        {hoveredRowIndex === index ? (
                            <span style={{ color: '#5a6acf', textDecoration: 'underline' }}>
                                View Details
                            </span>
                        ) : (
                            formatTimestamp(item.timestamp)
                        )}
                    </td>
                </tr>
            );
        });
    };

    // Dropdown menu style - extracted for consistency
    const dropdownMenuStyle = {
        maxHeight: '200px',
        overflowY: 'auto',
        padding: '8px',
        width: 'auto',
        minWidth: '180px'
    };

    // Checkbox item style - extracted for consistency
    const checkboxItemStyle = {
        display: 'flex',
        alignItems: 'center',
        padding: '6px 8px',
        borderRadius: '4px',
        transition: 'background-color 0.2s'
    };

    // Render multi-select dropdown with checkboxes
    const renderMultiSelectDropdown = (
        title,
        options,
        selectedValues,
        onToggle,
        isLoading = false
    ) => (
        <Dropdown>
            <Dropdown.Toggle
                variant="outline-secondary"
                id={`${title.toLowerCase()}-dropdown`}
                className="d-flex align-items-center"
                style={{
                    background: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    padding: '8px 14px',
                    fontSize: '14px',
                    color: '#5a6acf',
                    cursor: 'pointer',
                }}
            >
                {selectedValues.length > 0
                    ? `${title} (${selectedValues.length})`
                    : title
                }
                {isLoading && (
                    <span className="ms-2">
                        <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </span>
                )}
            </Dropdown.Toggle>
            <Dropdown.Menu style={dropdownMenuStyle}>
                <div className="dropdown-item-container">
                    {isLoading ? (
                        <div className="text-center p-2">
                            <div className="spinner-border spinner-border-sm" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <span className="ms-2">Loading options...</span>
                        </div>
                    ) : options.length === 0 ? (
                        <div className="text-center p-2">No options available</div>
                    ) : (
                        options.map(option => {
                            const isChecked = selectedValues.includes(option);
                            return (
                                <div
                                    key={option}
                                    className="dropdown-checkbox-item"
                                    style={{
                                        ...checkboxItemStyle,
                                        cursor: 'pointer'
                                    }}
                                    onClick={(e) => {
                                        // Prevent dropdown from closing and handle the toggle
                                        e.stopPropagation();
                                        onToggle(option);
                                    }}
                                >
                                    <Form.Check
                                        type="checkbox"
                                        id={`${title.toLowerCase()}-${option}`}
                                        checked={isChecked}
                                        onChange={() => { }} // Managed by parent div click handler
                                        onClick={(e) => {
                                            // Prevent double-firing of events when clicking directly on checkbox
                                            e.stopPropagation();
                                            onToggle(option);
                                        }}
                                        style={{ pointerEvents: 'none' }} // Make label part of the clickable area
                                        label={
                                            <span
                                                style={{
                                                    pointerEvents: 'none', // Prevent label from capturing click
                                                    cursor: 'pointer',
                                                    width: '100%',
                                                    display: 'inline-block'
                                                }}
                                            >
                                                {option}
                                            </span>
                                        }
                                    />
                                </div>
                            );
                        })
                    )}
                </div>
            </Dropdown.Menu>
        </Dropdown>
    );

    // New function for single-select timestamp dropdown
    const renderSingleSelectDropdown = (title, options, selectedValue, setSelectedValue) => (
        <Dropdown>
            <Dropdown.Toggle
                variant="outline-secondary"
                id={`${title.toLowerCase()}-dropdown`}
                className="d-flex align-items-center"
                style={{
                    background: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    padding: '8px 14px',
                    fontSize: '14px',
                    color: '#5a6acf',
                    cursor: 'pointer',
                }}
            >
                Timestamp
            </Dropdown.Toggle>
            <Dropdown.Menu style={dropdownMenuStyle}>

                {/* Timestamp options */}
                {options.map(option => (
                    <Dropdown.Item
                        key={option.value}
                        onClick={() => setSelectedValue(option.value)}
                        active={selectedValue === option.value}
                        style={{
                            backgroundColor: selectedValue === option.value ? "#5a6acf" : "",
                            color: selectedValue === option.value ? "white" : "inherit",
                            fontWeight: selectedValue === option.value ? "bold" : ""
                        }}
                    >
                        {option.label}
                    </Dropdown.Item>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    );

    // Calculate visible page numbers
    const totalPages = Math.ceil(totalRecords / itemsPerPage);
    const visiblePageNumbers = Array.from(
        { length: Math.min(pagesToShow, totalPages - currentRangeStart + 1) },
        (_, i) => currentRangeStart + i
    );

    // Helper function to truncate text
    const truncateDomain = (text, maxLength = 15) => {
        if (!text) return "";

        if (text.length <= maxLength) {
            return text;
        }

        return `${text.substring(0, maxLength)}...`;
    };

    return (
        <div style={{ 
            fontFamily: '"Inter", sans-serif', 
            background: '#f8f9fd', 
            margin: 0, 
            padding: '20px', 
            minHeight: '100vh',
            width: '100%',
            boxSizing: 'border-box'
        }}>
            <div style={{ 
                width: '100%',
                maxWidth: '100%',
                overflowX: 'auto',
                boxSizing: 'border-box'
            }}>
                <div style={{ 
                    background: '#fff', 
                    borderRadius: '16px', 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)', 
                    padding: '20px', 
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                }}>
                    {/* Add custom CSS for dropdown menus */}
                    <style jsx="true">{`
                        .dropdown-menu {
                            max-height: 200px;
                            overflow-y: auto;
                            scrollbar-width: thin;
                        }
                        
                        .dropdown-menu::-webkit-scrollbar {
                            width: 6px;
                        }
                        
                        .dropdown-menu::-webkit-scrollbar-track {
                            background: #f1f1f1;
                            border-radius: 10px;
                        }
                        
                        .dropdown-menu::-webkit-scrollbar-thumb {
                            background: #888;
                            border-radius: 10px;
                        }
                        
                        .dropdown-menu::-webkit-scrollbar-thumb:hover {
                            background: #555;
                        }
                        
                        .dropdown-checkbox-item:hover {
                            background-color: rgba(0, 123, 255, 0.1);
                        }
                        
                        /* Make sure the dropdown stays wide enough */
                        .dropdown {
                            margin-right: 8px;
                        }
                        
                        /* Make dropdown toggle buttons consistent size */
                        .dropdown-toggle {
                            min-width: 120px;
                            justify-content: space-between;
                        }

                        .form-check-input:checked {
                            background-color: #5a6acf !important;
                            border-color: #5a6acf !important;
                        }
                    `}</style>
                    {/* Header Section - Matched to SiteDocs.js */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '12px' }}>
                        <h2 style={{ fontSize: '20px', margin: 0, fontWeight: 600 }}>
                            Audit Logs - {truncateDomain(siteName)}
                        </h2>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            {/* Search Box with Button - Updated like ChatHistory.js */}
                            <div style={{ position: 'relative', width: '350px', minWidth: '250px' }}>
                                <input
                                    type="text"
                                    placeholder="Search audit logs..."
                                    value={searchInputValue}
                                    onChange={(e) => {
                                        setSearchInputValue(e.target.value);
                                        if (e.target.value.trim() === "") {
                                            setShowClearButton(false);
                                        }
                                    }}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSearch();
                                        }
                                    }}
                                    style={{
                                        width: '100%',
                                        padding: '8px 68px 8px 12px',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        fontSize: '14px'
                                    }}
                                />
                                <div style={{
                                    position: 'absolute',
                                    right: '4px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    display: 'flex',
                                    gap: '4px',
                                    alignItems: 'center'
                                }}>
                                    {showClearButton && (
                                        <Button
                                            variant="outline-secondary"
                                            onClick={handleClearSearch}
                                            disabled={isSearchingRef.current}
                                            style={{
                                                background: '#5a6acf',
                                                borderRadius: '8px',
                                                width: '28px',
                                                height: '28px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: '0',
                                                cursor: isSearchingRef.current ? 'not-allowed' : 'pointer',
                                                color: '#5a6acf',
                                                transition: 'all 0.2s ease-in-out',
                                                border: '1px solid #ddd',
                                                backgroundColor: 'white',
                                                boxShadow: '0 1px 1px rgba(0, 0, 0, 0.15)',
                                                opacity: isSearchingRef.current ? 0.6 : 1
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isSearchingRef.current) {
                                                    e.currentTarget.style.backgroundColor = '#5a6acf';
                                                    e.currentTarget.style.color = 'white';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isSearchingRef.current) {
                                                    e.currentTarget.style.backgroundColor = 'white';
                                                    e.currentTarget.style.color = '#5a6acf';
                                                }
                                            }}
                                        >
                                            <ClearIcon />
                                        </Button>
                                    )}
                                    <Button
                                        variant="primary"
                                        onClick={handleSearch}
                                        disabled={isSearchingRef.current}
                                        style={{
                                            background: '#5a6acf',
                                            borderRadius: '8px',
                                            width: '28px',
                                            height: '28px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '0',
                                            cursor: isSearchingRef.current ? 'not-allowed' : 'pointer',
                                            color: '#5a6acf',
                                            transition: 'all 0.2s ease-in-out',
                                            border: '1px solid #ddd',
                                            backgroundColor: 'white',
                                            boxShadow: '0 1px 1px rgba(0, 0, 0, 0.15)',
                                            opacity: isSearchingRef.current ? 0.6 : 1
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isSearchingRef.current) {
                                                e.currentTarget.style.backgroundColor = '#5a6acf';
                                                e.currentTarget.style.color = 'white';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isSearchingRef.current) {
                                                e.currentTarget.style.backgroundColor = 'white';
                                                e.currentTarget.style.color = '#5a6acf';
                                            }
                                        }}
                                    >
                                        <SearchIcon />
                                    </Button>
                                </div>
                            </div>

                            {/* Type Filter */}
                            {renderMultiSelectDropdown(
                                'Type',
                                AUDIT_ENTITIES,
                                selectedAuditEntities,
                                (value) => toggleSelection(value, selectedAuditEntities, setSelectedAuditEntities)
                            )}

                            {/* Operation Filter */}
                            {renderMultiSelectDropdown(
                                'Operation',
                                OPERATIONS,
                                selectedOperations,
                                (value) => toggleSelection(value, selectedOperations, setSelectedOperations)
                            )}

                            {/* Users Filter - now uses the API data */}
                            {renderMultiSelectDropdown(
                                'Users',
                                users,
                                selectedUsers,
                                (value) => toggleSelection(value, selectedUsers, setSelectedUsers),
                                usersLoading
                            )}

                            {/* Timestamp Filter - Now single select */}
                            {renderSingleSelectDropdown(
                                'Timestamp',
                                TIMESTAMP_OPTIONS,
                                selectedTimestamp,
                                setSelectedTimestamp
                            )}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="tooltip-refresh">Refresh audit logs</Tooltip>}
                            >
                                <Button
                                    variant="outline-secondary"
                                    onClick={refreshData}
                                    style={{
                                        background: 'white',
                                        border: '1px solid #ddd',
                                        borderRadius: '50%',
                                        width: '40px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0',
                                        fontSize: '14px',
                                        color: '#5a6acf',
                                        cursor: 'pointer',
                                        flexShrink: 0
                                    }}
                                >
                                    <RefreshIcon />
                                </Button>
                            </OverlayTrigger>

                            <Button
                                as={Link}
                                to="/site/docs"
                                state={{
                                    siteId: siteId,
                                    siteName: siteName
                                }}
                                variant="outline-secondary"
                                style={{
                                    background: 'white',
                                    border: '1px solid #ddd',
                                    borderRadius: '50%',
                                    width: '40px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0',
                                    fontSize: '14px',
                                    color: '#5a6acf',
                                    cursor: 'pointer',
                                    flexShrink: 0
                                }}
                            >
                                <BackIcon />
                            </Button>
                        </div>
                    </div>

                    {/* Active Filters Display */}
                    {(selectedAuditEntities.length > 0 ||
                        selectedOperations.length > 0 ||
                        selectedUsers.length > 0 ||
                        selectedTimestamp ||
                        searchTerm) && (
                            <div style={{ 
                                marginBottom: '15px', 
                                display: 'flex', 
                                flexWrap: 'wrap', 
                                gap: '8px' 
                            }}>
                                {selectedUsers.map(user => (
                                    <Badge
                                        key={user}
                                        bg="secondary"
                                        style={{ 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            padding: '6px 12px',
                                            borderRadius: '16px',
                                            fontSize: '12px'
                                        }}
                                    >
                                        {user}
                                        <button
                                            onClick={() => toggleSelection(user, selectedUsers, setSelectedUsers)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: 'white',
                                                marginLeft: '5px',
                                                cursor: 'pointer',
                                                padding: '0 4px',
                                                fontSize: '14px'
                                            }}
                                        >
                                            &times;
                                        </button>
                                    </Badge>
                                ))}
                                {selectedAuditEntities.map(entity => (
                                    <Badge
                                        key={entity}
                                        bg="secondary"
                                        style={{ 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            padding: '6px 12px',
                                            borderRadius: '16px',
                                            fontSize: '12px'
                                        }}
                                    >
                                        {entity}
                                        <button
                                            onClick={() => toggleSelection(entity, selectedAuditEntities, setSelectedAuditEntities)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: 'white',
                                                marginLeft: '5px',
                                                cursor: 'pointer',
                                                padding: '0 4px',
                                                fontSize: '14px'
                                            }}
                                        >
                                            &times;
                                        </button>
                                    </Badge>
                                ))}
                                {selectedOperations.map(operation => (
                                    <Badge
                                        key={operation}
                                        bg="secondary"
                                        style={{ 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            padding: '6px 12px',
                                            borderRadius: '16px',
                                            fontSize: '12px'
                                        }}
                                    >
                                        {operation}
                                        <button
                                            onClick={() => toggleSelection(operation, selectedOperations, setSelectedOperations)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: 'white',
                                                marginLeft: '5px',
                                                cursor: 'pointer',
                                                padding: '0 4px',
                                                fontSize: '14px'
                                            }}
                                        >
                                            &times;
                                        </button>
                                    </Badge>
                                ))}
                                {selectedTimestamp && (
                                    <Badge
                                        bg="secondary"
                                        style={{ 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            padding: '6px 12px',
                                            borderRadius: '16px',
                                            fontSize: '12px'
                                        }}
                                    >
                                        {TIMESTAMP_OPTIONS.find(opt => opt.value === selectedTimestamp)?.label || selectedTimestamp}
                                        <button
                                            onClick={() => setSelectedTimestamp("")}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: 'white',
                                                marginLeft: '5px',
                                                cursor: 'pointer',
                                                padding: '0 4px',
                                                fontSize: '14px'
                                            }}
                                        >
                                            &times;
                                        </button>
                                    </Badge>
                                )}
                                {searchTerm && (
                                    <Badge
                                        bg="info"
                                        style={{ 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            padding: '6px 12px',
                                            borderRadius: '16px',
                                            fontSize: '12px'
                                        }}
                                    >
                                        Search: {searchTerm}
                                        <button
                                            onClick={handleClearSearch}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: 'white',
                                                marginLeft: '5px',
                                                cursor: 'pointer',
                                                padding: '0 4px',
                                                fontSize: '14px'
                                            }}
                                        >
                                            &times;
                                        </button>
                                    </Badge>
                                )}
                            </div>
                        )}

                    {/* Table Section - Wrapped in scrollable container */}
                    <div style={{ width: '100%', overflowX: 'auto' }}>
                        <table style={{ 
                            width: '100%', 
                            borderCollapse: 'collapse', 
                            minWidth: '800px' 
                        }}>
                            <thead>
                                <tr style={{ textAlign: 'left', fontWeight: 600, fontSize: '14px', color: '#555' }}>
                                    <th style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', width: '20%' }}>User</th>
                                    <th style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', width: '20%' }}>Type</th>
                                    <th style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', width: '20%' }}>Operation</th>
                                    <th style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', width: '30%' }}>Entity Name</th>
                                    <th style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', width: '10%', textAlign: 'center' }}>Timestamp</th>
                                </tr>
                            </thead>
                            <tbody>
                                {renderTableContent()}
                            </tbody>
                        </table>
                    </div>

                    {auditData.length > 0 && (
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            marginTop: '15px', 
                            fontSize: '14px', 
                            color: '#888',
                            flexWrap: 'wrap',
                            gap: '10px'
                        }}>
                            <div style={{ flex: '1 1 200px', textAlign: 'left' }}>
                                <span>Showing data {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalRecords)} of {totalRecords} entries</span>
                            </div>

                            <div style={{ 
                                flex: '1 1 auto', 
                                display: 'flex', 
                                justifyContent: 'center', 
                                gap: '6px', 
                                alignItems: 'center',
                                flexWrap: 'wrap'
                            }}>
                                <button
                                    onClick={() => paginate(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    style={{
                                        padding: '4px 8px',
                                        border: '1px solid #ddd',
                                        borderRadius: '6px',
                                        background: '#fff',
                                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                        fontSize: '13px',
                                        opacity: currentPage === 1 ? 0.5 : 1
                                    }}
                                >
                                    &lt;
                                </button>

                                {visiblePageNumbers.map((number) => (
                                    <button
                                        key={number}
                                        onClick={() => paginate(number)}
                                        style={{
                                            padding: '4px 12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            background: currentPage === number ? '#5A6ACF' : '#fff',
                                            color: currentPage === number ? '#fff' : 'inherit',
                                            cursor: 'pointer',
                                            fontSize: '13px'
                                        }}
                                    >
                                        {number}
                                    </button>
                                ))}

                                {currentRangeStart + pagesToShow <= totalPages && (
                                    <span>...</span>
                                )}

                                <button
                                    onClick={() => paginate(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    style={{
                                        padding: '4px 8px',
                                        border: '1px solid #ddd',
                                        borderRadius: '6px',
                                        background: '#fff',
                                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                        fontSize: '13px',
                                        opacity: currentPage === totalPages ? 0.5 : 1
                                    }}
                                >
                                    &gt;
                                </button>
                            </div>

                            <div style={{ flex: '1 1 200px', textAlign: 'right' }}>
                                <label htmlFor="itemsPerPage" style={{ marginRight: '10px' }}>Items per page:</label>
                                <select
                                    id="itemsPerPage"
                                    value={itemsPerPage}
                                    onChange={handleItemsPerPageChange}
                                    style={{
                                        padding: '6px 12px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        height: '32px',
                                        width: '80px',
                                        fontSize: '13px',
                                        appearance: 'menulist'
                                    }}
                                >
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value={150}>150</option>
                                    <option value={200}>200</option>
                                    <option value={500}>500</option>
                                    <option value={1000}>1000</option>
                                </select>
                            </div>
                        </div>
                    )}

                    {/* Record Details Modal */}
                    {selectedRecord && (
                        <Modal show={showModal} onHide={closeRecordModal} centered size="lg">
                            <Modal.Header closeButton>
                                <Modal.Title>Audit Log Details</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                        <div>
                                            <p style={{ margin: 0, fontWeight: 600 }}>User:</p>
                                            <p style={{ margin: 0 }}>{selectedRecord.user_name}</p>
                                        </div>
                                        <div>
                                            <p style={{ margin: 0, fontWeight: 600 }}>Role:</p>
                                            <p style={{ margin: 0 }}>{selectedRecord.role}</p>
                                        </div>
                                        <div>
                                            <p style={{ margin: 0, fontWeight: 600 }}>Email:</p>
                                            <p style={{ margin: 0 }}>{selectedRecord.email}</p>
                                        </div>
                                        <div>
                                            <p style={{ margin: 0, fontWeight: 600 }}>Type:</p>
                                            <p style={{ margin: 0 }}>{selectedRecord.audit_entity}</p>
                                        </div>
                                        <div>
                                            <p style={{ margin: 0, fontWeight: 600 }}>Operation:</p>
                                            <span
                                                style={{
                                                    display: 'inline-block',
                                                    padding: '4px 12px',
                                                    borderRadius: '12px',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    ...getOperationBadgeStyle(selectedRecord.operation)
                                                }}
                                            >
                                                {selectedRecord.operation}
                                            </span>
                                        </div>
                                        <div>
                                            <p style={{ margin: 0, fontWeight: 600 }}>Timestamp:</p>
                                            <p style={{ margin: 0 }}>{formatTimestamp(selectedRecord.timestamp)}</p>
                                        </div>
                                    </div>
                                    <div>
                                        <p style={{ margin: 0, fontWeight: 600 }}>Entity Name:</p>
                                        <p style={{ margin: 0 }}>{selectedRecord.entity_name}</p>
                                    </div>
                                    <div>
                                        <p style={{ margin: 0, fontWeight: 600 }}>Changes:</p>
                                        <pre
                                            style={{
                                                backgroundColor: '#f4f4f4',
                                                padding: '10px',
                                                borderRadius: '5px',
                                                whiteSpace: 'pre-wrap',
                                                wordWrap: 'break-word',
                                                marginTop: '8px',
                                                fontSize: '13px'
                                            }}
                                        >
                                            {selectedRecord.changes}
                                        </pre>
                                    </div>
                                </div>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button 
                                    variant="secondary" 
                                    onClick={closeRecordModal} 
                                    style={{ 
                                        border: '1px solid #ddd',
                                        background: 'white',
                                        color: '#555'
                                    }}
                                >
                                    Close
                                </Button>
                            </Modal.Footer>
                        </Modal>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AuditLogs;